import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { assistantQueryKeys as queryKeys } from '../api/queryKeys'
import {
  createChatFolder,
  deleteChatFolder,
  getChatFolderThreads,
  getChatFolders,
  moveChatThread,
  renameChatFolder,
} from '../api/chatFolders.api'
import type { ChatFoldersResponse, ChatThreadsResponse } from '../api/contracts'

/** The caller's chat folders, by name, with create, rename and delete, and
 * filing a thread in one. Mutations answer with the fresh list, applied
 * straight to the cache (as useChatThreads does). */
export function useChatFolders() {
  const queryClient = useQueryClient()

  const foldersQuery = useQuery({
    queryKey: queryKeys.assistantChatFolders,
    queryFn: getChatFolders,
  })

  const applyFolders = (data: ChatFoldersResponse) => {
    queryClient.setQueryData(queryKeys.assistantChatFolders, data)
  }

  const createMutation = useMutation({
    mutationFn: (name: string) => createChatFolder({ name }),
    onSuccess: applyFolders,
  })

  const renameMutation = useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      renameChatFolder(folderId, { name }),
    onSuccess: applyFolders,
  })

  const deleteMutation = useMutation({
    mutationFn: (folderId: string) => deleteChatFolder(folderId),
    onSuccess: (data, folderId) => {
      applyFolders(data)
      queryClient.removeQueries({ queryKey: queryKeys.assistantChatFolderThreads(folderId) })
      // Its threads are unfiled now: the list carries each thread's folder.
      void queryClient.invalidateQueries({ queryKey: queryKeys.assistantChatThreads })
    },
  })

  const moveMutation = useMutation({
    mutationFn: ({ threadId, folderId }: { threadId: string; folderId: string | null }) =>
      moveChatThread(threadId, { folder_id: folderId }),
    onSuccess: (data: ChatThreadsResponse) => {
      queryClient.setQueryData(queryKeys.assistantChatThreads, data)
      void queryClient.invalidateQueries({ queryKey: queryKeys.assistantChatFolderThreadsAll })
    },
  })

  return {
    folders: foldersQuery.data?.folders ?? [],
    isLoading: foldersQuery.isLoading,
    createFolder: createMutation.mutateAsync,
    renameFolder: renameMutation.mutateAsync,
    deleteFolder: deleteMutation.mutateAsync,
    moveThread: moveMutation.mutateAsync,
  }
}

/** Every conversation filed in one of the caller's folders, from its own
 * query rather than the history list, which holds only the most recent. */
export function useChatFolderThreads(folderId: string) {
  const threadsQuery = useQuery({
    queryKey: queryKeys.assistantChatFolderThreads(folderId),
    queryFn: () => getChatFolderThreads(folderId),
  })

  return {
    threads: threadsQuery.data?.threads ?? [],
    isLoading: threadsQuery.isLoading,
    isError: threadsQuery.isError,
  }
}
